import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, MapPin, AlertTriangle, HeartPulse, User, ExternalLink, Loader2, HelpCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmergencyViewPage() {
  const { braceletCode } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [notificationSent, setNotificationSent] = useState(false);

  useEffect(() => {
    getLocationThenFetch();
  }, [braceletCode]);

  const getLocationThenFetch = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setLocation(loc);
          setLocationLoading(false);
          fetchEmergencyInfo(loc);
        },
        () => {
          setLocationLoading(false);
          fetchEmergencyInfo(null);
        },
        { timeout: 5000 }
      );
    } else {
      setLocationLoading(false);
      fetchEmergencyInfo(null);
    }
  };

  const fetchEmergencyInfo = async (loc) => {
    try {
      let url = `${API}/emergency/${braceletCode}`;
      if (loc) {
        url += `?lat=${loc.lat}&lng=${loc.lng}`;
      }
      const response = await axios.get(url);
      setProfile(response.data);
      if (response.data.is_registered) {
        setNotificationSent(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load emergency information");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = () => {
    if (profile?.emergency_contact_phone) {
      window.location.href = `tel:${profile.emergency_contact_phone}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-teal-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading emergency information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bracelet Not Found
            </h1>
            <p className="text-slate-600 mb-6">{error}</p>
            <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer">
              <Button className="bg-teal-600 hover:bg-teal-700">
                Visit Stress-Free Aging
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not registered - show registration prompt
  if (!profile?.is_registered) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-amber-200 bg-amber-50">
          <CardContent className="pt-6 text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-16 w-auto mx-auto mb-2"
            />
            <h2 className="text-teal-600 font-bold text-2xl mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              SOS Emergency ID
            </h2>
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bracelet Not Registered
            </h1>
            <p className="text-slate-600 mb-2">
              Bracelet Code: <span className="font-mono font-semibold">{braceletCode?.toUpperCase()}</span>
            </p>
            <p className="text-slate-600 mb-6">
              This bracelet has not been set up yet. Please register it to add emergency contact information.
            </p>
            <Link to={`/register-bracelet/${braceletCode}`}>
              <Button className="w-full bg-teal-600 hover:bg-teal-700 h-14 text-lg font-semibold" data-testid="register-bracelet-btn">
                Register This Bracelet
              </Button>
            </Link>
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-sm text-slate-600 mb-3">Need help or resources?</p>
              <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center justify-center gap-1">
                <ExternalLink className="h-4 w-4" />
                Visit stressfreeaging.com
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Registered - show emergency info
  return (
    <div className="min-h-screen bg-white">
      {/* Emergency Header */}
      <div className="bg-red-500 text-white py-3 px-4">
        <div className="flex items-center justify-center gap-3">
          <AlertTriangle className="h-5 w-5" />
          <span className="font-semibold uppercase tracking-wide">Emergency Contact Information</span>
        </div>
      </div>

      {/* Logo Banner */}
      <div className="bg-teal-50 border-b border-teal-100 py-3 px-4">
        <div className="flex items-center justify-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
            alt="Stress-Free Aging" 
            className="h-10 w-auto"
          />
          <span className="text-teal-700 font-bold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>SOS</span>
        </div>
      </div>

      <div className="container-app py-6 pb-44">
        {/* Dementia Alert */}
        {profile.has_dementia && (
          <div className="dementia-alert rounded-lg p-4 mb-6 animate-fade-in" data-testid="dementia-alert">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-pink-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-pink-800">Memory Care Alert</p>
                <p className="text-sm text-pink-700">This person may have dementia or memory issues. Please be patient and reassuring.</p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Section */}
        <div className="text-center mb-8 animate-slide-up">
          {profile.photo_base64 ? (
            <img
              src={`data:image/jpeg;base64,${profile.photo_base64}`}
              alt={profile.name}
              className="profile-photo-large mx-auto mb-4"
              data-testid="profile-photo"
            />
          ) : (
            <div className="w-40 h-40 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-teal-600">
              <User className="h-20 w-20 text-teal-600" />
            </div>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="profile-name">
            {profile.name}
          </h1>
          <p className="text-slate-500 mt-1">Bracelet: {braceletCode?.toUpperCase()}</p>
        </div>

        {/* Emergency Contact Card */}
        <Card className="border-slate-200 mb-6 animate-slide-up stagger-1" data-testid="emergency-contact-card">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Emergency Contact
            </h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-900" data-testid="contact-name">{profile.emergency_contact_name}</p>
                  {profile.emergency_contact_relationship && (
                    <p className="text-sm text-slate-500">{profile.emergency_contact_relationship}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                  <Phone className="h-5 w-5 text-teal-600" />
                </div>
                <p className="font-medium text-slate-900" data-testid="contact-phone">{profile.emergency_contact_phone}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Allergies Card */}
        {profile.allergies && (
          <Card className="border-amber-200 bg-amber-50 mb-6 animate-slide-up stagger-2" data-testid="allergies-card">
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-amber-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Allergies / Medical Notes
              </h2>
              <p className="text-amber-900">{profile.allergies}</p>
            </CardContent>
          </Card>
        )}

        {/* Location Card */}
        <Card className="border-slate-200 mb-6 animate-slide-up stagger-3" data-testid="location-card">
          <CardContent className="pt-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <MapPin className="h-5 w-5 text-teal-600" />
              Current Location
            </h2>
            {locationLoading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Getting location...</span>
              </div>
            ) : location ? (
              <div>
                <p className="text-sm text-slate-600 mb-3">
                  Coordinates: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </p>
                <a
                  href={`https://www.google.com/maps?q=${location.lat},${location.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
                  data-testid="open-maps-link"
                >
                  <MapPin className="h-4 w-4" />
                  Open in Google Maps
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <p className="text-slate-500">Location not available. Please enable location services.</p>
            )}
          </CardContent>
        </Card>

        {/* Stress-Free Aging Resources Section */}
        <Card className="border-teal-200 bg-teal-50 mb-6 animate-slide-up stagger-4" data-testid="resources-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
                alt="Stress-Free Aging" 
                className="h-12 w-auto mx-auto mb-4"
              />
              <h2 className="text-lg font-semibold text-teal-800 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Need Additional Support?
              </h2>
              <p className="text-teal-700 mb-4 text-sm">
                Stress-Free Aging provides resources and guidance for caregivers and families of seniors.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="https://stressfreeaging.com/about" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full sm:w-auto border-teal-600 text-teal-700 hover:bg-teal-100">
                    Caregiver Resources
                  </Button>
                </a>
                <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700 text-white">
                    Learn More
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stress-Free Aging Link */}
        <div className="text-center animate-slide-up stagger-4">
          <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium">
            <HeartPulse className="h-5 w-5" />
            Powered by Stress-Free Aging
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      {/* Fixed Call Button - positioned above platform badge */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-lg" style={{ zIndex: 9999 }}>
        <Button
          onClick={handleCall}
          className="w-full h-16 bg-red-500 hover:bg-red-600 text-white text-xl font-bold rounded-xl emergency-pulse flex items-center justify-center gap-3"
          data-testid="emergency-call-btn"
        >
          <Phone className="h-7 w-7" />
          CALL {profile.emergency_contact_name?.split(' ')[0]?.toUpperCase() || 'EMERGENCY CONTACT'}
        </Button>
      </div>
    </div>
  );
}
